/**
 * Notice generator engine — paper presets, fonts, layout and the single
 * Canvas2D renderer shared by the live preview and the PNG export.
 *
 * Why one renderer: the preview draws into an on-screen <canvas> at a smaller
 * pixel size and the download draws into an off-screen one at full size, both
 * through `renderNotice`. There is no second code path that could drift, and a
 * notice looks on the sheet exactly like it looked in the panel.
 *
 * Why Canvas2D and not an SVG string: the notice text is Bengali, and an SVG
 * loaded through an <img> gets no web fonts, so `<text>` would rasterise in a
 * fallback face with broken conjuncts. `fillText` uses the browser's own text
 * engine, which shapes যুক্তাক্ষর correctly. The frame and the pictogram carry
 * no text, so those stay SVG and are drawn in as images.
 */
import { buildFrameSvg, getFrameById } from './noticeFrameLibrary';
import { encodeCanvas } from '../shared/imageOutputFormats';
import { buildIconSvg, detectIconId } from './noticeIconLibrary';
import {
    FONT_CATALOG, FONT_SCRIPTS, ensureFontFamily,
} from '../../../../../features/print-template-editor/fonts';

export const MAX_TEXT_LENGTH = 220;

/** Prohibition rings are red on every theme — that is what makes them read. */
export const RING_COLOR = '#D92B2B';

/* ------------------------------------------------------------------ *
 *  Paper
 * ------------------------------------------------------------------ */

// 150 dpi: enough to print an A4 notice sharply without a 20 MB PNG.
export const PAPER_PRESETS = [
    { id: 'a4Landscape', width: 1754, height: 1240 },
    { id: 'a4Portrait', width: 1240, height: 1754 },
    { id: 'square', width: 1400, height: 1400 },
    { id: 'wide', width: 1920, height: 1080 },
];

export function getPaperById(id) {
    return PAPER_PRESETS.find((paper) => paper.id === id) || PAPER_PRESETS[0];
}

/* ------------------------------------------------------------------ *
 *  Fonts
 *
 *  The picker offers the print editor's whole generated catalogue, so loading
 *  goes through that module too rather than this tool keeping a second, smaller
 *  list and its own <link> injector.
 * ------------------------------------------------------------------ */

/**
 * The Bangla name for each family the picker offers.
 *
 * A Bengali reader looks for "হিন্দ শিলিগুড়ি", not "Hind Siliguri", so the row
 * leads with that and keeps the Latin name alongside. A family without an entry
 * here falls back to its own name, which is what a newly-added one would do.
 */
export const BN_FONT_NAMES = {
    'Hind Siliguri': 'হিন্দ শিলিগুড়ি',
    'Noto Sans Bengali': 'নোটো সান্স বাংলা',
    'Noto Serif Bengali': 'নোটো সেরিফ বাংলা',
    'Anek Bangla': 'অনেক বাংলা',
    'Baloo Da 2': 'বালু দা ২',
    Atma: 'আত্মা',
    Mina: 'মিনা',
    Alkatra: 'আলকাত্রা',
    'Tiro Bangla': 'তিরো বাংলা',
    Galada: 'গালাদা',
    'Google Sans': 'গুগল সান্স',
};

/** What a fresh notice is set in, and the fallback for anything unresolvable. */
export const DEFAULT_FONT_FAMILY = 'Hind Siliguri';

const BENGALI_BIT = 1 << FONT_SCRIPTS.findIndex(([key]) => key === 'bengali');

/**
 * The families the picker offers: every one in the catalogue that actually
 * carries Bengali glyphs, and nothing else.
 *
 * Of the ~1,950 families in the library only eleven have the script. Offering
 * the rest looked generous and was not: a Latin-only face has no Bengali
 * letters, so the canvas silently falls back and the choice does nothing.
 * Derived from the catalogue rather than hand-listed, so a family gained in a
 * future regeneration appears on its own.
 */
export const NOTICE_FONT_FAMILIES = FONT_CATALOG
    .filter((fam) => fam.google && (fam.scriptMask & BENGALI_BIT) !== 0)
    .map((fam) => fam.name);

const loadedFonts = new Set();

/**
 * Load a family and wait until its faces are actually usable.
 *
 * Every measurement below goes through `measureText`, which silently falls back
 * to a system face while the web font is still in flight — so a notice measured
 * too early would be laid out for the wrong metrics and come out mis-sized.
 *
 * `ensureFontFamily` appends a stylesheet <link>; its @font-face rules are NOT
 * in the document by the time it returns, and `document.fonts.load` against a
 * face the registry has never heard of resolves happily with nothing loaded.
 * That is why this polls `check` instead of awaiting `load` once — Noto Serif
 * Bengali rendered in the fallback face without it.
 *
 * Both cuts are awaited because the Bold switch picks between them at draw time,
 * and they are probed with a Bengali glyph so a subset that lacks the script
 * cannot report itself ready.
 */
const FONT_WAIT_TRIES = 24;
const FONT_WAIT_STEP = 50;

export async function ensureNoticeFont(family) {
    const name = family || DEFAULT_FONT_FAMILY;
    if (loadedFonts.has(name)) return;
    if (typeof document === 'undefined' || !document.fonts?.load) return;

    ensureFontFamily(name);

    const specs = [`700 64px "${name}"`, `400 64px "${name}"`];
    // Every family here carries Bengali, so the probe is a Bengali glyph: a
    // subset that lacks the script cannot then report itself ready.
    const probe = 'অ';

    for (let i = 0; i < FONT_WAIT_TRIES; i += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            await Promise.all(specs.map((spec) => document.fonts.load(spec, probe)));
            if (specs.every((spec) => document.fonts.check(spec, probe))) break;
        } catch {
            // A blocked stylesheet must not break the tool: the notice still
            // renders in the fallback Bengali face the site already ships.
            break;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { setTimeout(resolve, FONT_WAIT_STEP); });
    }

    loadedFonts.add(name);
}

/* ------------------------------------------------------------------ *
 *  Layout options
 * ------------------------------------------------------------------ */

export const ICON_POSITIONS = ['right', 'left', 'above', 'below', 'none'];
export const ICON_SIZES = { small: 0.26, medium: 0.36, large: 0.48 };
export const EMPHASIS_MODES = ['none', 'firstSmall', 'lastSmall', 'firstBig'];

/**
 * Text size as a percentage of the largest that fits, NOT an absolute px value.
 *
 * The notice is laid out by fitting the text to the box, and that maximum
 * differs per paper preset, per pictogram position and per line count. A px
 * slider would mean "42px" overflowing an A4-tall sheet and swimming on a 16:9
 * one; a percentage of the fitted size always fits and always means the same
 * thing — 100% fills the box, less leaves air around the words.
 */
export const TEXT_SCALE = { min: 40, max: 100, step: 5 };

/** Per-line size multipliers for an emphasis mode. */
function lineScales(count, emphasis) {
    const scales = new Array(count).fill(1);
    if (count < 2) return scales;
    if (emphasis === 'firstSmall') scales[0] = 0.62;
    if (emphasis === 'lastSmall') scales[count - 1] = 0.62;
    if (emphasis === 'firstBig') {
        for (let i = 1; i < count; i += 1) scales[i] = 0.66;
    }
    return scales;
}

export const COLOR_THEMES = [
    { id: 'classic', background: '#FFFFFF', text: '#111111', frame: '#111111', icon: '#111111' },
    { id: 'navy', background: '#FFFFFF', text: '#12275C', frame: '#12275C', icon: '#12275C' },
    { id: 'red', background: '#FFFFFF', text: '#B21C1C', frame: '#B21C1C', icon: '#B21C1C' },
    { id: 'green', background: '#FFFFFF', text: '#0E5C3A', frame: '#0E5C3A', icon: '#0E5C3A' },
    { id: 'caution', background: '#FFD400', text: '#111111', frame: '#111111', icon: '#111111' },
    { id: 'dark', background: '#141414', text: '#FFFFFF', frame: '#FFFFFF', icon: '#FFFFFF' },
];

export const CUSTOM_THEME_ID = 'custom';

/** Where the custom picker starts — also what the swatch tile shows. */
export const DEFAULT_CUSTOM_COLORS = {
    background: '#FFFFFF',
    text: '#111111',
    frame: '#111111',
    icon: '#111111',
};

export function getThemeById(id) {
    return COLOR_THEMES.find((theme) => theme.id === id) || COLOR_THEMES[0];
}

/** The four colours a notice actually draws with — preset or hand-picked. */
export function resolveTheme(state) {
    if (state?.themeId === CUSTOM_THEME_ID) {
        return { id: CUSTOM_THEME_ID, ...DEFAULT_CUSTOM_COLORS, ...(state.customColors || {}) };
    }
    return getThemeById(state?.themeId);
}

/* ------------------------------------------------------------------ *
 *  Ready-made notices
 * ------------------------------------------------------------------ */

/**
 * One-click starting points. Each fills the whole form, so a user who only
 * wants "no smoking on the wall by lunchtime" never has to touch a control.
 */
export const NOTICE_SAMPLES = [
    { id: 'noSmoking', text: 'এখানে ধূমপান\nকরা নিষেধ', frameId: 'ornate', iconId: 'noSmoking', iconPosition: 'right', themeId: 'classic', emphasis: 'none' },
    { id: 'noPhoto', text: 'ছবি তোলা সম্পূর্ণ\nনিষেধ', frameId: 'ornate', iconId: 'noPhoto', iconPosition: 'right', themeId: 'classic', emphasis: 'none' },
    { id: 'shoesOff', text: 'দয়া করে\nজুতা খুলে ভিতরে\nপ্রবেশ করুন', frameId: 'ornate', iconId: 'noShoes', iconPosition: 'left', themeId: 'classic', emphasis: 'firstSmall' },
    { id: 'prayerPhone', text: 'দয়া করে\nনামাজের সময়\nমোবাইল ফোন বন্ধ রাখুন!', frameId: 'double', iconId: 'noMobile', iconPosition: 'below', themeId: 'classic', emphasis: 'firstSmall' },
    { id: 'cctv', text: 'নিরাপত্তার স্বার্থে এই স্থানটি\nসার্বক্ষণিক সিসি ক্যামেরার\nনজরদারিতে রয়েছে।', frameId: 'ornate', iconId: 'cctv', iconPosition: 'right', themeId: 'classic', emphasis: 'none' },
    { id: 'electricity', text: 'দয়া করে\nবিদ্যুৎ অপচয়\nকরবেন না', frameId: 'ornate', iconId: 'saveElectricity', iconPosition: 'right', themeId: 'classic', emphasis: 'firstSmall' },
    { id: 'noEntry', text: 'অনুমতি ছাড়া\nপ্রবেশ নিষেধ', frameId: 'band', iconId: 'noEntry', iconPosition: 'left', themeId: 'red', emphasis: 'firstSmall' },
    { id: 'noParking', text: 'এখানে গাড়ি\nপার্কিং নিষেধ', frameId: 'studded', iconId: 'noParking', iconPosition: 'right', themeId: 'red', emphasis: 'none' },
    { id: 'keepClean', text: 'যত্রতত্র ময়লা ফেলবেন না\nডাস্টবিন ব্যবহার করুন', frameId: 'rounded', iconId: 'noLittering', iconPosition: 'left', themeId: 'green', emphasis: 'none' },
    { id: 'silence', text: 'অনুগ্রহ করে\nনীরবতা বজায় রাখুন', frameId: 'double', iconId: 'silence', iconPosition: 'right', themeId: 'navy', emphasis: 'firstSmall' },
    { id: 'caution', text: 'সাবধান\nমেঝে ভেজা আছে', frameId: 'brackets', iconId: 'wetFloor', iconPosition: 'left', themeId: 'caution', emphasis: 'firstBig' },
    { id: 'idCard', text: 'প্রবেশের সময়\nপরিচয়পত্র প্রদর্শন করুন', frameId: 'single', iconId: 'idCard', iconPosition: 'right', themeId: 'navy', emphasis: 'firstSmall' },
];

/** Default state for a fresh notice — also what Reset returns to. */
export const DEFAULT_NOTICE = {
    text: '',
    frameId: 'ornate',
    iconId: 'auto',
    iconPosition: 'right',
    iconSize: 'medium',
    fontFamily: DEFAULT_FONT_FAMILY,
    textScale: TEXT_SCALE.max,
    themeId: 'classic',
    customColors: DEFAULT_CUSTOM_COLORS,
    emphasis: 'none',
    // OFF by default: where the author put the breaks IS the design of a notice.
    // "এখানে ধূমপান / করা নিষেধ" typed as four lines came out as two, because
    // auto-balancing throws the newlines away and re-sets the words as one
    // stream. The toggle is still there for anyone who wants that.
    autoLines: false,
    paperId: 'a4Landscape',
    bold: true,
    // Uploaded artwork (data URLs). A custom frame is stretched over the whole
    // sheet; a custom pictogram is fitted into the pictogram box.
    customFrameUrl: null,
    customFrameName: '',
    customIconUrl: null,
    customIconName: '',
};

export const CUSTOM_FRAME_ID = 'custom';
export const CUSTOM_ICON_ID = 'custom';

/** Inset a custom frame keeps clear — matched to the built-in double rule. */
const CUSTOM_FRAME_INSET = 0.14;

/**
 * The pictogram a notice will actually draw.
 * 'auto' resolves from the text; 'custom' only counts once a file is uploaded.
 */
export function resolveIconId(state) {
    if (state.iconPosition === 'none') return null;
    if (state.iconId === CUSTOM_ICON_ID) return state.customIconUrl ? CUSTOM_ICON_ID : null;
    if (state.iconId && state.iconId !== 'auto') return state.iconId;
    return detectIconId(state.text);
}

/* ------------------------------------------------------------------ *
 *  Text layout
 * ------------------------------------------------------------------ */

function fontSpec(size, family, bold) {
    return `${bold ? 700 : 400} ${size}px "${family}", "Hind Siliguri", "Noto Sans Bengali", sans-serif`;
}

/* ------------------------------------------------------------------ *
 *  Balanced typesetting
 *
 *  Greedy wrapping fills each line before starting the next, which on a poster
 *  gives the classic ragged result — a full line, then a stub. A notice is read
 *  as a block, so the lines want to come out near-equal instead, and the line
 *  COUNT itself is part of the decision: two long lines or three short ones,
 *  whichever ends up largest on the sheet.
 *
 *  Text is measured once at a reference size and scaled arithmetically —
 *  `measureText` is linear in font size, so one pass of measurement serves
 *  every candidate arrangement instead of re-measuring inside a search.
 * ------------------------------------------------------------------ */

const REF_SIZE = 100;
const MAX_AUTO_LINES = 6;

/** Word widths (and the space between them) at REF_SIZE. */
function measureWords(ctx, words, family, bold) {
    ctx.font = fontSpec(REF_SIZE, family, bold);
    return {
        widths: words.map((word) => ctx.measureText(word).width),
        space: ctx.measureText(' ').width,
    };
}

/**
 * Split `words` into exactly `count` lines, minimising the widest line.
 *
 * Straight dynamic programming over (word index, lines used) — with a notice's
 * handful of words the table is tiny, and unlike a greedy pass it cannot paint
 * itself into a corner and leave one word stranded on the last line.
 *
 * `lineFactor(i)` is the emphasis multiplier for line i, so a line the notice
 * will draw at 62% is allowed proportionally more words.
 *
 * @returns {{lines: number[][], cost: number} | null} word index ranges per line
 */
function balancedPartition(widths, space, count, lineFactor) {
    const n = widths.length;
    if (count > n) return null;

    // rawWidth[i][j] — width of words i..j-1 set on one line, at REF_SIZE.
    const rawWidth = [];
    for (let i = 0; i < n; i += 1) {
        rawWidth[i] = [];
        let sum = 0;
        for (let j = i; j < n; j += 1) {
            sum += widths[j] + (j > i ? space : 0);
            rawWidth[i][j + 1] = sum;
        }
    }

    // dp[j][i] — best achievable "widest scaled line" using the first i words
    // in j lines; from[j][i] remembers where that last line started.
    const dp = Array.from({ length: count + 1 }, () => new Array(n + 1).fill(Infinity));
    const from = Array.from({ length: count + 1 }, () => new Array(n + 1).fill(-1));
    dp[0][0] = 0;

    for (let j = 1; j <= count; j += 1) {
        const factor = lineFactor(j - 1);
        for (let i = j; i <= n; i += 1) {
            for (let k = j - 1; k < i; k += 1) {
                if (dp[j - 1][k] === Infinity) continue;
                // A line set smaller may hold more words before it is "wide".
                const lineCost = rawWidth[k][i] * factor;
                const cost = Math.max(dp[j - 1][k], lineCost);
                if (cost < dp[j][i]) {
                    dp[j][i] = cost;
                    from[j][i] = k;
                }
            }
        }
    }

    if (dp[count][n] === Infinity) return null;

    const lines = [];
    const lineWidths = [];
    let i = n;
    for (let j = count; j > 0; j -= 1) {
        const k = from[j][i];
        lines.unshift([k, i]);
        lineWidths.unshift(rawWidth[k][i] * lineFactor(j - 1));
        i = k;
    }
    return { lines, lineWidths, cost: dp[count][n] };
}

/**
 * Set the whole text, choosing the line count as well as the breaks.
 *
 * Every count from one line up is costed and scored on `size × evenness`, not
 * on size alone. Size alone is what produces the ragged poster: on a box where
 * height is the binding constraint, one more line always measures "bigger",
 * even when it buys that by stranding a word on a line of its own. Multiplying
 * by how even the lines came out lets a slightly smaller, squarer block win —
 * which is the one that reads as designed.
 *
 * Ties go to the fewer lines, since the loop runs upward and only a strictly
 * better score replaces the incumbent.
 */
function autoBalancedLayout(ctx, words, {
    maxWidth, maxHeight, family, bold, emphasis, lineGap,
}) {
    const { widths, space } = measureWords(ctx, words, family, bold);
    const maxLines = Math.min(MAX_AUTO_LINES, words.length);
    let best = null;

    for (let count = 1; count <= maxLines; count += 1) {
        const factors = lineScales(count, emphasis);
        const partition = balancedPartition(widths, space, count, (i) => factors[i]);
        if (!partition) continue;

        // Width caps the size through the widest (emphasis-scaled) line; height
        // caps it through the stack. The smaller cap is the size that fits.
        const byWidth = (maxWidth * REF_SIZE) / partition.cost;
        const byHeight = maxHeight / (lineGap * factors.reduce((sum, f) => sum + f, 0));
        const size = Math.min(byWidth, byHeight);

        // Evenness: mean line width over the widest, so 1 is a perfect block and
        // a stray one-word line drags it down.
        const mean = partition.lineWidths.reduce((sum, x) => sum + x, 0) / count;
        const evenness = partition.cost > 0 ? mean / partition.cost : 1;
        const score = size * evenness;

        if (!best || score > best.score) {
            best = { score, size, count, partition, factors };
        }
    }

    if (!best || !(best.size > 0)) return { lines: [], lineHeight: 0, totalHeight: 0 };

    const size = Math.max(6, Math.floor(best.size));
    const lines = best.partition.lines.map(([from, to], i) => ({
        text: words.slice(from, to).join(' '),
        size: Math.max(6, Math.round(size * best.factors[i])),
    }));

    return {
        lines,
        lineHeight: lineGap,
        totalHeight: lines.reduce((sum, line) => sum + line.size * lineGap, 0),
    };
}

/** Break one paragraph into lines that fit `maxWidth` at `size`. */
function wrapParagraph(ctx, paragraph, size, family, bold, maxWidth) {
    ctx.font = fontSpec(size, family, bold);
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) return [];

    const lines = [];
    let current = words[0];

    for (let i = 1; i < words.length; i += 1) {
        const candidate = `${current} ${words[i]}`;
        if (ctx.measureText(candidate).width <= maxWidth) {
            current = candidate;
        } else {
            lines.push(current);
            current = words[i];
        }
    }
    lines.push(current);
    return lines;
}

/**
 * Fit the notice text into a box.
 *
 * With `autoLines` (the default) the breaks are the engine's to choose: the
 * words are re-set as balanced lines at whatever count sits largest on the
 * sheet. Turn it off and the user's own line breaks are authored decisions,
 * kept as written — only a line too wide even on its own gets wrapped, and the
 * base size is searched downwards until the stack fits.
 *
 * @returns {{lines: {text: string, size: number}[], lineHeight: number, totalHeight: number}}
 */
export function layoutNoticeText(ctx, text, {
    maxWidth,
    maxHeight,
    family,
    bold = true,
    emphasis = 'none',
    lineGap = 1.34,
    autoLines = true,
}) {
    const paragraphs = String(text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

    if (!paragraphs.length || maxWidth <= 0 || maxHeight <= 0) {
        return { lines: [], lineHeight: 0, totalHeight: 0 };
    }

    if (autoLines) {
        // Newlines become plain word breaks: the whole notice is one stream to
        // set, which is the only way the line count can be chosen at all.
        const words = paragraphs.join(' ').split(/\s+/).filter(Boolean);
        if (words.length) {
            return autoBalancedLayout(ctx, words, {
                maxWidth, maxHeight, family, bold, emphasis, lineGap,
            });
        }
    }

    const scales = lineScales(paragraphs.length, emphasis);

    /**
     * The largest base size at which every paragraph fits the box.
     *
     * With `allowWrap` off a paragraph has to fit on ONE line or the size loses,
     * which is what keeps an authored break authored. `fits` is monotone in the
     * size either way, so ~14 probes land within a pixel.
     */
    const search = (allowWrap) => {
        let lo = 8;
        let hi = Math.ceil(maxHeight);
        let best = null;

        while (lo <= hi) {
            const size = Math.floor((lo + hi) / 2);
            const laid = [];
            let ok = true;

            for (let i = 0; i < paragraphs.length && ok; i += 1) {
                const lineSize = Math.max(6, Math.round(size * scales[i]));
                const wrapped = allowWrap
                    ? wrapParagraph(ctx, paragraphs[i], lineSize, family, bold, maxWidth)
                    : [paragraphs[i]];
                if (!wrapped.length) { ok = false; break; }
                // `wrapParagraph` leaves this font set; the no-wrap path never
                // called it, so measuring here would otherwise use a stale size.
                ctx.font = fontSpec(lineSize, family, bold);
                // Too wide for the box means this size loses: an unbreakable word
                // when wrapping is on, the whole line when it is off.
                for (const line of wrapped) {
                    if (ctx.measureText(line).width > maxWidth) { ok = false; break; }
                    laid.push({ text: line, size: lineSize });
                }
            }

            const totalHeight = laid.reduce((sum, line) => sum + line.size * lineGap, 0);
            if (ok && totalHeight <= maxHeight) {
                best = { lines: laid, lineHeight: lineGap, totalHeight };
                lo = size + 1;
            } else {
                hi = size - 1;
            }
        }
        return best;
    };

    // Keep every typed line whole and shrink to make it fit — "ছবি তোলা সম্পূর্ণ /
    // নিষেধ" was coming out as three lines because a bigger size that wrapped the
    // first line still "fit". Only when no size can hold a line whole (one very
    // long paragraph, or a pasted sentence) does wrapping come back.
    return search(false) || search(true) || { lines: [], lineHeight: 0, totalHeight: 0 };
}

/* ------------------------------------------------------------------ *
 *  Rendering
 * ------------------------------------------------------------------ */

/** Load any image URL. Uploads arrive as data URLs, so nothing is fetched. */
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image-load-failed'));
        img.src = src;
    });
}

function loadSvgImage(svg) {
    return loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
}

/** Fit `img` inside a square box without distorting it (CSS `object-fit: contain`). */
function containRect(img, x, y, side) {
    const ratio = (img.naturalWidth || 1) / (img.naturalHeight || 1);
    const w = ratio >= 1 ? side : side * ratio;
    const h = ratio >= 1 ? side / ratio : side;
    return { x: x + (side - w) / 2, y: y + (side - h) / 2, w, h };
}

/**
 * Draw a notice onto a canvas at `width` × `height` device pixels.
 *
 * The canvas is resized here, so a caller only has to hand over the node.
 * @returns {Promise<void>}
 */
export async function renderNotice(canvas, state, { width, height } = {}) {
    const paper = getPaperById(state.paperId);
    const w = Math.round(width || paper.width);
    const h = Math.round(height || paper.height);

    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    const theme = resolveTheme(state);
    const family = state.fontFamily || DEFAULT_FONT_FAMILY;
    const usingCustomFrame = state.frameId === CUSTOM_FRAME_ID && !!state.customFrameUrl;
    const frame = getFrameById(state.frameId);

    await ensureNoticeFont(family);

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, w, h);

    if (usingCustomFrame) {
        try {
            // An uploaded border is artwork for the whole sheet, so it is
            // stretched edge to edge rather than fitted — the same thing a
            // border PNG does when used as a page background.
            ctx.drawImage(await loadImage(state.customFrameUrl), 0, 0, w, h);
        } catch {
            /* an unreadable upload just leaves the notice unframed */
        }
    } else if (frame.id !== 'none' && state.frameId !== CUSTOM_FRAME_ID) {
        try {
            const frameImg = await loadSvgImage(buildFrameSvg(frame.id, w, h, theme.frame));
            ctx.drawImage(frameImg, 0, 0, w, h);
        } catch {
            /* a frame that fails to rasterise just leaves a plain notice */
        }
    }

    const short = Math.min(w, h);
    const inset = (state.frameId === CUSTOM_FRAME_ID ? CUSTOM_FRAME_INSET : frame.inset) * short;
    const contentX = inset;
    const contentY = inset;
    const contentW = w - inset * 2;
    const contentH = h - inset * 2;

    const iconId = resolveIconId(state);
    const position = iconId ? state.iconPosition : 'none';
    const iconSide = iconId
        ? (ICON_SIZES[state.iconSize] ?? ICON_SIZES.medium) * Math.min(contentW, contentH)
        : 0;
    const gap = iconId ? short * 0.04 : 0;

    // Split the content box between the pictogram and the text.
    let textBox = { x: contentX, y: contentY, w: contentW, h: contentH };
    let iconBox = null;

    if (iconId && position === 'right') {
        textBox = { x: contentX, y: contentY, w: contentW - iconSide - gap, h: contentH };
        iconBox = { x: contentX + contentW - iconSide, y: contentY + (contentH - iconSide) / 2, side: iconSide };
    } else if (iconId && position === 'left') {
        textBox = { x: contentX + iconSide + gap, y: contentY, w: contentW - iconSide - gap, h: contentH };
        iconBox = { x: contentX, y: contentY + (contentH - iconSide) / 2, side: iconSide };
    } else if (iconId && position === 'above') {
        textBox = { x: contentX, y: contentY + iconSide + gap, w: contentW, h: contentH - iconSide - gap };
        iconBox = { x: contentX + (contentW - iconSide) / 2, y: contentY, side: iconSide };
    } else if (iconId && position === 'below') {
        textBox = { x: contentX, y: contentY, w: contentW, h: contentH - iconSide - gap };
        iconBox = { x: contentX + (contentW - iconSide) / 2, y: contentY + contentH - iconSide, side: iconSide };
    }

    if (iconBox) {
        try {
            if (iconId === CUSTOM_ICON_ID) {
                const img = await loadImage(state.customIconUrl);
                const fit = containRect(img, iconBox.x, iconBox.y, iconBox.side);
                ctx.drawImage(img, fit.x, fit.y, fit.w, fit.h);
            } else {
                const iconImg = await loadSvgImage(buildIconSvg(iconId, {
                    color: theme.icon,
                    hole: theme.background,
                    ringColor: RING_COLOR,
                }));
                ctx.drawImage(iconImg, iconBox.x, iconBox.y, iconBox.side, iconBox.side);
            }
        } catch {
            /* keep the text even if the pictogram cannot be drawn */
        }
    }

    const layout = layoutNoticeText(ctx, state.text, {
        maxWidth: textBox.w,
        maxHeight: textBox.h,
        family,
        bold: state.bold !== false,
        emphasis: state.emphasis,
        autoLines: state.autoLines !== false,
    });

    if (!layout.lines.length) return;

    // The scale is applied AFTER the fit, so the user's line breaks survive:
    // shrinking before wrapping would let long lines re-flow and the notice
    // would reshape itself every time the slider moved.
    const scale = Math.min(TEXT_SCALE.max, Math.max(TEXT_SCALE.min, state.textScale ?? TEXT_SCALE.max)) / 100;
    const lines = scale === 1
        ? layout.lines
        : layout.lines.map((line) => ({ ...line, size: line.size * scale }));
    const totalHeight = layout.totalHeight * scale;

    ctx.fillStyle = theme.text;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    const centerX = textBox.x + textBox.w / 2;
    let cursorY = textBox.y + (textBox.h - totalHeight) / 2;

    for (const line of lines) {
        const lineHeight = line.size * layout.lineHeight;
        ctx.font = fontSpec(line.size, family, state.bold !== false);
        // Baseline sits a little above the bottom of the line box so Bengali
        // matra and descenders both stay inside it.
        ctx.fillText(line.text, centerX, cursorY + lineHeight - line.size * 0.34);
        cursorY += lineHeight;
    }
}

/** Draw the notice off-screen at the full paper size. */
async function renderFullCanvas(state) {
    const paper = getPaperById(state.paperId);
    const canvas = document.createElement('canvas');
    await renderNotice(canvas, state, { width: paper.width, height: paper.height });
    return canvas;
}

/** Render at full paper size and return a PNG blob. */
export async function renderNoticePngBlob(state, format = 'png') {
    const canvas = await renderFullCanvas(state);
    return (await encodeCanvas(canvas, format)).blob;
}

/** Render at full paper size as a PNG data URL — what the print sheet embeds. */
export async function renderNoticeDataUrl(state) {
    const canvas = await renderFullCanvas(state);
    return canvas.toDataURL('image/png');
}

export function outputFileName(ext = 'png') {
    return `notice.${ext}`;
}

/* ------------------------------------------------------------------ *
 *  Uploads
 * ------------------------------------------------------------------ */

export const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

/**
 * Read a picked frame/pictogram file into a data URL.
 *
 * A data URL (rather than an object URL) is what keeps the export self-contained:
 * the canvas never touches a blob: origin, so `toBlob` cannot taint, and the
 * artwork survives a re-render without a live object reference.
 *
 * @returns {Promise<{dataUrl: string, name: string}>}
 * @throws {Error} 'type' for a non-image, 'size' when over the cap
 */
export function readImageFile(file) {
    if (!file || !/^image\//.test(file.type)) return Promise.reject(new Error('type'));
    if (file.size > MAX_UPLOAD_BYTES) return Promise.reject(new Error('size'));

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ dataUrl: String(reader.result), name: file.name });
        reader.onerror = () => reject(new Error('read'));
        reader.readAsDataURL(file);
    });
}

export function isNoticeReady(state) {
    return Boolean(String(state?.text || '').trim());
}
